import Fuse from "fuse.js";
import { useMemo, useState } from "react";
import {
  GetMarketsSummary200Response,
  InstrumentTypeResponse,
} from "../../codegen-api";
import { IMarket } from "../../contexts/MarketContext";
import { useGetMarketSummary } from "../../hooks/api/marketSummary/useGetMarketSummary";
import usePersistentState from "../../hooks/usePersistentState";
import { useGetIndexHistories } from "../../hooks/api/indexHistories/useGetIndexHistories";
import { useGetMarkets } from "../../contexts/MarketInstrumentContext/useGetMarkets";
import { isInstrumentTypeEnabled } from "../../config/features";
import { ISectionData, MarketFiltersEnum } from "./CommandModal.types";
import { computeSectionsData } from "./CommandModal.sections";

export function useMarketsData(
  market: IMarket,
  watchlist: IMarket[],
  t: (key: string) => string,
  tTradingType: (key: string) => string
) {
  const { preferredMarketFilter: rawMarketFilter, setPreferredMarketFilter } =
    usePersistentState();
  // Fallback to ALL_MARKETS if stored filter is stale/invalid
  const marketFilter = Object.values(MarketFiltersEnum).includes(rawMarketFilter as MarketFiltersEnum)
    ? rawMarketFilter as MarketFiltersEnum
    : MarketFiltersEnum.ALL_MARKETS;

  const { data: marketsSummary, mutate: mutateMarketSummary } =
    useGetMarketSummary();

  // Which verticals this build offers comes from the feature-flag registry
  // (registry default -> chain override -> env override), not a chain-id list.
  const isPerpEnabled = isInstrumentTypeEnabled(
    InstrumentTypeResponse.Perpetual
  );
  const isSpotEnabled = isInstrumentTypeEnabled(InstrumentTypeResponse.Spot);

  // TEMPFIX: fallback use unique assets from /markets
  const { data: marketsData } = useGetMarkets(
    undefined,
    InstrumentTypeResponse.Perpetual,
    !isPerpEnabled
  );

  const { data: spotMarketsData } = useGetMarkets(
    undefined,
    InstrumentTypeResponse.Spot,
    !isSpotEnabled
  );

  const uniqueAssets = useMemo(
    () =>
      Array.from(
        new Set(marketsData?.map((s) => s.underlying_asset) || [])
      ).sort(),
    [marketsData]
  );

  const uniqueSpotAssets = useMemo(
    () =>
      Array.from(
        new Set(spotMarketsData?.map((s) => s.underlying_asset) || [])
      ).sort(),
    [spotMarketsData]
  );

  const marketMetaByAsset = useMemo(
    () =>
      new Map(
        (marketsData || []).map((m) => [
          m.underlying_asset,
          {
            displayName: m.display_name,
            category: m.market_category,
          },
        ])
      ),
    [marketsData]
  );

  // Fallback when markets summary is slow
  const marketsSummaryData = useMemo(() => {
    if (!marketsSummary) {
      const fallbackSummaries: GetMarketsSummary200Response = {
        summaries: uniqueAssets.map((a) => {
          const mkt = marketsData?.find((d) => a === d.underlying_asset);
          return {
            asset: a,
            index_price: mkt?.index_price || "",
            index_daily_change: "",
            option_info: {
              // "open_interest"?: {
              //   // 'calls'?: string;
              //   // 'puts'?: string;
              //   "total": "0"
              // },
              mark_price: mkt?.mark_price || "",
              mark_daily_change: "",
              price_step: mkt?.price_step || "",
              amount_step: mkt?.amount_step || "",
            },
            perpetual_info: {
              // 'open_interest'?: GetMarketsSummary200ResponseSummariesInnerPerpetualInfoOpenInterest;
              mark_price: mkt?.mark_price || "",
              mark_daily_change: "",
              // 'pre_launch'?: boolean;
              // 'daily_volume'?: string;
              price_step: mkt?.price_step || "",
              amount_step: mkt?.amount_step || "",
            },
          };
        }),
      };
      return fallbackSummaries;
    }
    return marketsSummary;
  }, [marketsData, marketsSummary, uniqueAssets]);

  const { data: assetIndexHistories, mutate: mutateIndexHistories } =
    useGetIndexHistories(uniqueAssets);

  const [marketsSummaryLastUpdatedMillis, setMarketsSummaryLastUpdatedMillis] =
    useState(Date.now());

  const [searchText, setSearchText] = useState("");
  const [highlightedMarketIndex, setHighlightedMarketIndex] = useState<{
    sectionIndex: number;
    dataIndex: number;
  }>({
    sectionIndex: 0,
    dataIndex: 0,
  });

  const searchResults = useMemo(() => {
    const summaries = (marketsSummaryData?.summaries || []).map((summary) => {
      const meta = marketMetaByAsset.get(summary.asset);
      return {
        ...summary,
        display_name: meta?.displayName,
        market_category: meta?.category,
      };
    });

    if (searchText) {
      const fuse = new Fuse(summaries, {
        keys: ["asset", "display_name", "market_category"],
        shouldSort: true,
        threshold: 0.5,
      });
      const search = fuse.search(searchText);
      return search.map((v) => v.item);
    }
    return summaries;
  }, [marketMetaByAsset, marketsSummaryData?.summaries, searchText]);

  const sectionsData: ISectionData[] = useMemo(
    () =>
      computeSectionsData({
        searchResults,
        watchlist,
        market,
        marketFilter,
        assetIndexHistories,
        t,
        tTradingType,
        searchText,
        uniqueSpotAssets,
        spotMarketsData,
      }),
    [
      assetIndexHistories?.assets_history,
      market.asset,
      market.derivative,
      marketFilter,
      searchResults,
      searchText,
      spotMarketsData,
      uniqueSpotAssets,
      t,
      tTradingType,
      watchlist,
    ]
  );

  return {
    marketFilter,
    setPreferredMarketFilter,
    mutateMarketSummary,
    mutateIndexHistories,
    marketsSummaryLastUpdatedMillis,
    setMarketsSummaryLastUpdatedMillis,
    searchText,
    setSearchText,
    highlightedMarketIndex,
    setHighlightedMarketIndex,
    sectionsData,
  };
}
